import { useEffect, useRef } from 'react'

/**
 * Autofill do navegador, gerenciadores de senha e ferramentas de preenchimento
 * automático setam o `value` do input direto no DOM — muitas vezes SEM disparar
 * o evento `change`/`input` que o React escuta. Resultado: o state do componente
 * fica defasado e qualquer lógica reativa ao valor (ex.: detectar domínio FCamara
 * pra alternar entre senha e SSO 365) não reage, mesmo com o campo preenchido.
 *
 * Este hook devolve um `ref` pra plugar no input e mantém o state sincronizado
 * com o valor REAL do DOM, lendo periodicamente enquanto o campo está montado.
 * Cobre autofill nativo (Chrome/Safari/Firefox), extensões e ferramentas externas
 * que não emitem evento algum. O custo é uma comparação de string a cada 300ms.
 *
 * @param value  valor atual no state (pra comparar e evitar setState redundante)
 * @param setter setter do state (ex.: `setEmail`) — estável entre renders
 */
export function useAutofillSync(value: string, setter: (v: string) => void) {
  const ref = useRef<HTMLInputElement>(null)
  const valueRef = useRef(value)
  valueRef.current = value

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const sync = () => {
      if (el.value !== valueRef.current) setter(el.value)
    }

    sync() // captura autofill que já ocorreu antes do efeito rodar
    const id = window.setInterval(sync, 300)
    return () => window.clearInterval(id)
  }, [setter])

  return ref
}
